import React, { useState, useEffect } from "react"
import { LabeledTextField } from "app/core/components/LabeledTextField"
import { Form, FORM_ERROR } from "app/core/components/Form"
import login from "../mutations/login"
import { LoginInput } from "../validations"
import { useMutation } from "@blitzjs/rpc"
import Link from "next/link"
import SplitScreenLayout from "app/core/layouts/SplitScreenLayout"
import { GoogleOAuthProvider } from "@react-oauth/google"
import GoogleOAuth from "app/auth/components/GoogleAuth"
import { User } from "@prisma/client"
import { DialogContent, DialogOverlay } from "@reach/dialog"
import { Button } from "app/core/components/Button"
import loginNewGoogleUser from "../mutations/loginNewGoogleUser"
import loginWithGoogle from "../mutations/loginWithGoogle"

type LoginFormProps = {
  onSuccess?: (loginUser: Omit<User, "hashedPassword">) => void
}

export const LoginForm = (props: LoginFormProps) => {
  // throwOnError defaults to true in Blitz's useMutation wrapper, which makes
  // React Query also throw this error during the next render so the nearest
  // ErrorBoundary can catch it. app/_app.tsx has a root ErrorBoundary that
  // catches AuthenticationError and renders a fresh <LoginForm>, which was
  // remounting this component (wiping the just-set submit error) every time
  // login failed with bad credentials. We already handle the error locally
  // below, so opt this mutation out of that global behavior.
  const [loginMutation] = useMutation(login, { throwOnError: false })
  const [loginWithGoogleMutation] = useMutation(loginWithGoogle, { throwOnError: false })
  const [loginNewGoogleUserMutation, { isPending: isCreatingGoogleUser }] = useMutation(
    loginNewGoogleUser,
    { throwOnError: false }
  )
  const [googleCredential, setGoogleCredential] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = React.useState("")
  const [showProfilePrompt, setShowProfilePrompt] = useState(false)

  useEffect(() => {
    if (googleCredential) {
      const handleLogin = async () => {
        try {
          const result = await loginWithGoogleMutation({ credential: googleCredential })
          if (!result.profileExists) {
            setShowProfilePrompt(true)
          } else if (props.onSuccess) {
            props.onSuccess(result.user)
          }
        } catch (error) {
          setErrorMessage("An error occurred during login.")
        }
      }
      handleLogin()
    }
  }, [googleCredential, loginWithGoogleMutation]) // eslint-disable-line react-hooks/exhaustive-deps

  const onClose = () => {
    setGoogleCredential(null)
    setShowProfilePrompt(false)
  }

  const onConfirm = async () => {
    if (isCreatingGoogleUser || !googleCredential) return
    try {
      const newUser = await loginNewGoogleUserMutation({ credential: googleCredential })
      setShowProfilePrompt(false)
      if (props.onSuccess) {
        props.onSuccess(newUser)
      }
    } catch (error) {
      setShowProfilePrompt(false)
      setErrorMessage(error.message || "An error occurred while creating your account.")
    }
  }

  return (
    <GoogleOAuthProvider clientId={process.env.GOOGLE_OAUTH_CLIENTID || ""}>
      <SplitScreenLayout heading="Log in to your account" className="space-y-8">
        <Form
          submitText="Log In"
          schema={LoginInput}
          initialValues={{ email: undefined, password: undefined }}
          onSubmit={async (values) => {
            try {
              const loginUser = await loginMutation(values)
              if (props.onSuccess) {
                props.onSuccess(loginUser)
              }
            } catch (error) {
              if (error.name === "AuthenticationError") {
                return { [FORM_ERROR]: "Sorry, those credentials are invalid" }
              } else {
                return {
                  [FORM_ERROR]:
                    "Sorry, we had an unexpected error. Please try again. - " + error.toString(),
                }
              }
            }
          }}
        >
          <LabeledTextField
            name="email"
            label="Email"
            placeholder="Email"
            autoComplete="email"
            type="email"
          />
          <LabeledTextField
            name="password"
            label="Password"
            placeholder="Password"
            type="password"
            autoComplete="password"
          />

          <div className="flex items-center justify-end mt-6">
            <div className="text-sm leading-5">
              <Link
                href="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500 "
              >
                Forgot your password?
              </Link>
            </div>
          </div>
        </Form>

        <GoogleOAuth setCredential={setGoogleCredential} setErrorMessage={setErrorMessage} />
        {errorMessage && <div className="pl-1 text-sm text-red-700">{errorMessage}</div>}

        <DialogOverlay
          isOpen={showProfilePrompt}
          onDismiss={() => setShowProfilePrompt(false)}
          className="modal--small"
        >
          <DialogContent aria-label="No account found">
            <div className="overflow-y-auto max-h-80 px-2 py-2">
              <h3 className="font-bold text-xl mb-2 pb-2 border-b border-gray-200">
                No account found
              </h3>
              <div>
                There was no account found with the Google email address. Would you like to create
                one?
              </div>
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="bg-gray-300 text-black opacity-90 hover:opacity-100"
                >
                  No
                </Button>
                <Button
                  type="button"
                  onClick={onConfirm}
                  disabled={isCreatingGoogleUser}
                  className="bg-green-500 opacity-90 hover:opacity-100"
                >
                  Yes
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogOverlay>
      </SplitScreenLayout>
    </GoogleOAuthProvider>
  )
}

export default LoginForm
